import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { Spinner } from "@/components/ui/Spinner";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useThreads } from "@/hooks/useThreads";

export const Route = createFileRoute("/_authenticated/chat/")({
  component: ChatIndex,
  validateSearch: (search: Record<string, unknown>) => ({
    template: (search.template as string) || undefined,
  }),
});

function ChatIndex() {
  const { threads, isLoading, createThread } = useThreads();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/chat/" });
  const { mode } = useOnboardingStatus();
  const hasRedirected = useRef(false);

  useEffect(() => {
    // The redirect is a ONE-SHOT decision, so it must not be taken on an
    // unknown mode: `mode === "loading"` would send a mid-ritual user down the
    // template branch and mint the second thread the lock exists to prevent.
    // This route only ever renders a spinner, so waiting one round trip for a
    // query that is already in flight costs nothing visible.
    if (isLoading || mode === "loading" || hasRedirected.current) return;
    hasRedirected.current = true;

    // A template requested MID-RITUAL is ignored, not honored: `/chat?template=`
    // is reachable during onboarding (the notebook's "ask agent" navigates
    // here), and it is the one new-thread path that is not a button we can
    // hide. Resume the onboarding thread instead of opening a second one.
    if (search.template && mode === "onboarding" && threads && threads.length > 0) {
      void navigate({
        to: "/chat/$threadId",
        params: { threadId: threads[0]._id as string },
        replace: true,
      });
      return;
    }

    // If a template is requested, always create a new thread for it
    if (search.template) {
      createThread({
        title: "New Chat",
        metadata: { templateId: search.template, templateName: search.template },
      })
        .then((threadId) => {
          void navigate({ to: "/chat/$threadId", params: { threadId: threadId as string } });
        })
        .catch(() => {});
      return;
    }

    // Resume the most recent thread if one exists
    if (threads && threads.length > 0) {
      void navigate({
        to: "/chat/$threadId",
        params: { threadId: threads[0]._id as string },
        replace: true,
      });
      return;
    }

    // No threads — create the first one
    createThread({ title: "New Chat" })
      .then((threadId) => {
        void navigate({ to: "/chat/$threadId", params: { threadId: threadId as string } });
      })
      .catch(() => {});
  }, [isLoading, mode, threads, createThread, navigate, search.template]);

  return <Spinner />;
}
